import * as THREE from 'three';
import type { Input } from '../core/Input';
import type { TerrainData } from '../world/TerrainData';

export type MoveMode = 'walk' | 'fly';

export interface CylinderCollider {
  x: number;
  z: number;
  r: number;
  y0: number;
  y1: number;
}

/** Uniform-grid spatial hash for vertical cylinder colliders (hoodoos, trunks, walls). */
export class ColliderGrid {
  private readonly cells = new Map<number, CylinderCollider[]>();
  constructor(private readonly cell = 16) {}
  private key(ix: number, iz: number) {
    return (ix + 32768) * 65536 + (iz + 32768);
  }
  add(c: CylinderCollider) {
    const x0 = Math.floor((c.x - c.r) / this.cell), x1 = Math.floor((c.x + c.r) / this.cell);
    const z0 = Math.floor((c.z - c.r) / this.cell), z1 = Math.floor((c.z + c.r) / this.cell);
    for (let ix = x0; ix <= x1; ix++) for (let iz = z0; iz <= z1; iz++) {
      const k = this.key(ix, iz);
      let a = this.cells.get(k);
      if (!a) this.cells.set(k, (a = []));
      a.push(c);
    }
  }
  query(x: number, z: number, out: CylinderCollider[]) {
    out.length = 0;
    const a = this.cells.get(this.key(Math.floor(x / this.cell), Math.floor(z / this.cell)));
    if (a) for (const c of a) out.push(c);
    return out;
  }
  get size() {
    return this.cells.size;
  }
}

export interface FootstepEvent {
  surface: 'trail' | 'rock' | 'ground' | 'snow' | 'paved';
  intensity: number;
}

export class Controller {
  mode: MoveMode = 'walk';
  readonly position = new THREE.Vector3(0, 2442, 0); // eye position
  readonly velocity = new THREE.Vector3();
  yaw = 0;
  pitch = 0;
  onGround = false;
  eyeHeight = 1.68;
  radius = 0.3;
  walkSpeed = 1.5;
  runSpeed = 4.2;
  flySpeed = 35;
  sensitivity = 0.0022;
  invertY = false;
  private bobPhase = 0;
  private bobAmp = 0;
  readonly bobOffset = new THREE.Vector3();
  onFootstep: ((e: FootstepEvent) => void) | null = null;
  surfaceAt: ((x: number, z: number) => FootstepEvent['surface']) | null = null;
  private readonly tmpCols: CylinderCollider[] = [];
  autopilot = false; // when true, position is driven externally

  constructor(private readonly terrain: TerrainData, private readonly colliders: ColliderGrid) {}

  groundHeight(x: number, z: number) {
    return this.terrain.heightAt(x, z);
  }

  teleport(x: number, z: number, yawDeg: number, pitchDeg = 0, heightAboveGround?: number) {
    const g = this.groundHeight(x, z);
    this.position.set(x, g + (heightAboveGround ?? this.eyeHeight), z);
    this.yaw = THREE.MathUtils.degToRad(yawDeg);
    this.pitch = THREE.MathUtils.degToRad(pitchDeg);
    this.velocity.set(0, 0, 0);
    this.onGround = this.mode === 'walk';
  }

  setMode(m: MoveMode) {
    if (m === this.mode) return;
    this.mode = m;
    this.velocity.set(0, 0, 0);
    if (m === 'walk') {
      // drop to the ground below
      this.onGround = false;
    } else {
      this.position.y += 2;
    }
  }

  get heightAboveGround() {
    return this.position.y - this.eyeHeight - this.groundHeight(this.position.x, this.position.z);
  }

  /** Forward direction on the horizontal plane (yaw 0 = looking north / -Z). */
  private forward(out: THREE.Vector3) {
    return out.set(Math.sin(this.yaw), 0, -Math.cos(this.yaw));
  }

  update(dt: number, input: Input) {
    // --- look
    const invert = this.invertY ? -1 : 1;
    this.yaw += input.lookDX * this.sensitivity;
    this.pitch -= input.lookDY * this.sensitivity * invert;
    this.pitch = THREE.MathUtils.clamp(this.pitch, -1.45, 1.45);
    if (this.autopilot) return;
    const k = input.keys;
    const fwdIn = input.axis(['KeyS', 'ArrowDown'], ['KeyW', 'ArrowUp']) + input.stick.y;
    const strafeIn = input.axis(['KeyA', 'ArrowLeft'], ['KeyD', 'ArrowRight']) + input.stick.x;
    const boost = k.has('ShiftLeft') || k.has('ShiftRight');
    const fwd = this.forward(new THREE.Vector3());
    const right = new THREE.Vector3(-fwd.z, 0, fwd.x);
    if (this.mode === 'fly') this.updateFly(dt, input, fwdIn, strafeIn, boost, right);
    else this.updateWalk(dt, input, fwdIn, strafeIn, boost, fwd, right);
  }

  private updateFly(dt: number, input: Input, f: number, s: number, boost: boolean, right: THREE.Vector3) {
    if (input.wheel !== 0) this.flySpeed = THREE.MathUtils.clamp(this.flySpeed * Math.pow(1.18, -input.wheel), 3, 900);
    const up = input.axis(['KeyC', 'ControlLeft', 'KeyQ'], ['Space', 'KeyE']) + (input.touchUp ? 1 : 0) - (input.touchDown ? 1 : 0);
    const look = new THREE.Vector3(Math.sin(this.yaw) * Math.cos(this.pitch), Math.sin(this.pitch), -Math.cos(this.yaw) * Math.cos(this.pitch));
    const wish = new THREE.Vector3().addScaledVector(look, f).addScaledVector(right, s);
    wish.y += up;
    if (wish.lengthSq() > 1) wish.normalize();
    const speed = this.flySpeed * (boost ? 4 : 1);
    wish.multiplyScalar(speed);
    const a = 1 - Math.exp(-dt * 3.5);
    this.velocity.lerp(wish, a);
    this.position.addScaledVector(this.velocity, dt);
    const g = this.groundHeight(this.position.x, this.position.z);
    const minY = g + 1.8;
    if (this.position.y < minY) {
      this.position.y = minY;
      if (this.velocity.y < 0) this.velocity.y = 0;
    }
    this.position.y = Math.min(this.position.y, 9000);
    this.resolveColliders(this.position.y - 1.0, this.position.y + 0.5, 1.0);
    this.bobOffset.set(0, 0, 0);
  }

  private updateWalk(dt: number, input: Input, f: number, s: number, boost: boolean, fwd: THREE.Vector3, right: THREE.Vector3) {
    const wish = new THREE.Vector3().addScaledVector(fwd, f).addScaledVector(right, s);
    if (wish.lengthSq() > 1) wish.normalize();
    const speed = boost ? this.runSpeed : this.walkSpeed;
    wish.multiplyScalar(speed);
    const accel = this.onGround ? 9 : 1.5;
    const a = 1 - Math.exp(-dt * accel);
    this.velocity.x += (wish.x - this.velocity.x) * a;
    this.velocity.z += (wish.z - this.velocity.z) * a;
    if (this.onGround && input.consumePress('Space')) this.velocity.y = 3.6;
    this.velocity.y -= 9.81 * dt;

    const feet = this.position.y - this.eyeHeight;
    const g0 = this.groundHeight(this.position.x, this.position.z);
    // horizontal move with slope limit (try full, then axis-separated)
    const tryMove = (dx: number, dz: number) => {
      const nx = this.position.x + dx, nz = this.position.z + dz;
      const g1 = this.groundHeight(nx, nz);
      const dist = Math.hypot(dx, dz);
      const rise = g1 - Math.max(g0, feet);
      if (this.onGround && dist > 1e-5 && rise > 0.35 + dist * 1.15) return false; // too steep (>~49deg)
      this.position.x = nx;
      this.position.z = nz;
      return true;
    };
    const dx = this.velocity.x * dt, dz = this.velocity.z * dt;
    if (!tryMove(dx, dz)) {
      if (!tryMove(dx, 0)) this.velocity.x = 0;
      if (!tryMove(0, dz)) this.velocity.z = 0;
    }
    this.position.y += this.velocity.y * dt;
    const g = this.groundHeight(this.position.x, this.position.z);
    const newFeet = this.position.y - this.eyeHeight;
    if (newFeet <= g + (this.onGround ? 0.45 : 0)) {
      // stick to ground (also when walking downhill)
      this.position.y = g + this.eyeHeight;
      if (this.velocity.y < 0) this.velocity.y = 0;
      this.onGround = true;
    } else {
      this.onGround = false;
    }
    this.resolveColliders(this.position.y - this.eyeHeight + 0.3, this.position.y, this.radius);

    // head bob + footsteps
    const hs = Math.hypot(this.velocity.x, this.velocity.z);
    const targetAmp = this.onGround ? Math.min(hs / 4.0, 1) : 0;
    this.bobAmp += (targetAmp - this.bobAmp) * (1 - Math.exp(-dt * 6));
    const prev = this.bobPhase;
    this.bobPhase += dt * (1.6 + hs * 0.55) * Math.PI * 2 * 0.5;
    if (this.onGround && hs > 0.3 && Math.floor(prev / Math.PI) !== Math.floor(this.bobPhase / Math.PI)) {
      const surface = this.surfaceAt ? this.surfaceAt(this.position.x, this.position.z) : 'ground';
      this.onFootstep?.({ surface, intensity: Math.min(1, 0.4 + hs / 4) });
    }
    this.bobOffset.set(
      Math.cos(this.bobPhase) * 0.025 * this.bobAmp,
      Math.abs(Math.sin(this.bobPhase)) * 0.045 * this.bobAmp,
      0,
    );
  }

  private resolveColliders(y0: number, y1: number, radius: number) {
    const cols = this.colliders.query(this.position.x, this.position.z, this.tmpCols);
    for (const c of cols) {
      if (y1 < c.y0 || y0 > c.y1) continue;
      const dx = this.position.x - c.x, dz = this.position.z - c.z;
      const d = Math.hypot(dx, dz);
      const minD = c.r + radius;
      if (d < minD) {
        const push = (minD - d) / Math.max(d, 1e-4);
        this.position.x += dx * push;
        this.position.z += dz * push;
        // remove inward velocity
        const nx = dx / Math.max(d, 1e-4), nz = dz / Math.max(d, 1e-4);
        const vn = this.velocity.x * nx + this.velocity.z * nz;
        if (vn < 0) {
          this.velocity.x -= vn * nx;
          this.velocity.z -= vn * nz;
        }
      }
    }
  }

  applyToCamera(cam: THREE.PerspectiveCamera) {
    const right = new THREE.Vector3(Math.cos(this.yaw), 0, Math.sin(this.yaw));
    cam.position.copy(this.position).addScaledVector(right, this.bobOffset.x);
    cam.position.y += this.bobOffset.y;
    const euler = new THREE.Euler(this.pitch, -this.yaw, 0, 'YXZ');
    cam.quaternion.setFromEuler(euler);
    cam.updateMatrixWorld();
  }
}
